/**
 * ویرایشِ نمایه — قرینه‌ی apps/mobile/app/profile.tsx.
 *
 * *خواندنِ* دفترچه‌ی مخاطبین همچنان اینجا نیست (مرورگر دفترچه ندارد)، ولی
 * «شماره‌های من» هست: ثبتش کاری می‌کند که دوستان پیدات کنند و هیچ ربطی به
 * دفترچه‌ی گوشی ندارد. نگاه کن به MyPhonesCard.tsx.
 */
import React, { useEffect, useState } from 'react';
import { BUILD_STAMP } from '@/core/lib/buildInfo';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { profileRepo } from '@/core/data/socialRepo';
import { useSession } from '@/core/session';
import { hasPasswordLogin } from '@/core/lib/changePassword';
import { t, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { handleFilterBlocked } from '@/ui/filterBlocked';
import { Avatar, Btn, Card, Field } from '@/ui/kit';
import { MyPhonesCard } from './MyPhonesCard';
import { pickSquareImage } from './imagePicker';

export function ProfilePage() {
  useLocale();
  const { session, profile, refreshProfile } = useSession();
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);

  const [name, setName] = useState('');
  useEffect(() => {
    if (!profile) return;
    setName(profile.displayName);
  }, [profile?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const changePhoto = useMutation({
    mutationFn: async (): Promise<'ok' | 'cancelled' | 'error'> => {
      const picked = await pickSquareImage();
      if (picked.kind === 'error') { setMsg(`${t('error')}: ${picked.message.slice(0, 120)}`); return 'error'; }
      if (picked.kind === 'cancelled') return 'cancelled';
      const old = profile!.avatarPath;
      const path = await profileRepo.uploadAvatar(profile!.id, picked.base64);
      await profileRepo.updateMine(profile!.id, { avatarPath: path });
      await profileRepo.removeAvatar(old);
      return 'ok';
    },
    onSuccess: async (r) => {
      if (r !== 'ok') return;
      await refreshProfile();
      qc.invalidateQueries();
      setMsg(t('photoSaved'));
    },
    onError: (e) => { reportError('web.profile.changePhoto', e); setMsg(t('errorRetry')); },
  });

  const dirty = !!profile && name.trim() !== profile.displayName;

  const saveInfo = useMutation({
    mutationFn: () => profileRepo.updateMine(profile!.id, { displayName: name.trim() }),
    onSuccess: async () => {
      await refreshProfile();
      qc.invalidateQueries();
      setMsg(t('profileSaved'));
    },
    onError: (e) => { if (handleFilterBlocked(e)) return; reportError('web.profile.saveInfo', e); setMsg(t('errorRetry')); },
  });

  if (!profile) return <div className="feed"><p className="dim">…</p></div>;

  return (
    <div className="feed">
      {/* نمایه فقط از تنظیمات باز می‌شود؛ بدون این، تنها راهِ برگشت دکمه‌ی
          مرورگر بود (WHOSUP-366). قرینه‌ی دکمه‌ی «برگشت» اپ. */}
      <Link to="/settings" className="dim">{t('back')}</Link>
      <h1 className="feed-title">{t('profile')}</h1>

      <div className="avatar-edit">
        <Avatar url={profile.avatarUrl} name={profile.displayName} size={92}
          onClick={() => changePhoto.mutate()} />
        <p className="dim">{changePhoto.isPending ? t('saving') : t('tapPhotoToChange')}</p>
      </div>

      <Card>
        <h3>{t('myProfile')}</h3>
        <Field label={t('displayName')} value={name} onChange={setName}
          placeholder={t('enterName')} />
        {/* نام کاربری عمداً قفله: دوستان با همین پیدات می‌کنن. */}
        <p className="dim">{t('usernameLocked', { u: profile.username })}</p>
        {msg ? <p className="dim">{msg}</p> : null}
        <Btn label={saveInfo.isPending ? t('saving') : t('save')}
          disabled={!dirty || !name.trim() || saveInfo.isPending}
          onClick={() => saveInfo.mutate()} />
      </Card>

      <MyPhonesCard />

      {/* شهر از این صفحه رفت به تنظیمات (‎/settings/city): «کجا زندگی می‌کنی»
          یه تنظیمِ خودِ اپه — فهرستِ کشف از همون‌جا پر می‌شه — نه یه فیلدِ
          دیگه‌ی کارتِ هویت.
          دکمه‌ها `btn` با قاب‌اند نه `ghost`: بی‌قاب، کنارِ هم شبیهِ یک خطِ
          متن بودند و معلوم نبود دکمه‌اند (بازخوردِ تستِ WHOSUP-367). */}
      <div className="chiprow chips-wrap">
        <Link to="/settings/city" className="btn">{t('defaultCity')}</Link>
        <Link to="/availability" className="btn">{t('availability')}</Link>
        <Link to="/settings/notifications" className="btn">{t('notifSettings')}</Link>
        <Link to="/notifications" className="btn">{t('notifHistory')}</Link>
        {/* فقط حسابِ ایمیل‌ورمز (WHOSUP-367): گوگل/اپل رمزی ندارد که عوض شود. */}
        {hasPasswordLogin(session?.user) ? (
          <Link to="/profile/password" className="btn">{t('changePassword')}</Link>
        ) : null}
      </div>
          {/* از کدام برنچ منتشر شده — قرینه‌ی سطرِ پایینِ ستینگِ اپ.
          فقط وقتی از مسیرِ publish رد شده باشد چیزی هست. */}
      {BUILD_STAMP ? (
        <p className="dim" style={{ textAlign: 'center', fontSize: 11 }}>
          WhosUp · {BUILD_STAMP}
        </p>
      ) : null}
</div>
  );
}
